import { useCallback, useEffect, useRef, useState } from "react";
import {
  JOURNAL_SECTION_MAX,
  type JournalAiMode,
  type JournalNewSource,
  type JournalSectionAiResult,
} from "@care-by-ella/contracts";
import { UnauthorizedError } from "@/lib/clinician-api";
import { removedSentencesNote } from "@/lib/journal-ai-gate";
import {
  approveNewSourceLabel,
  KEEP_JOURNAL_TEXT,
  newSourceCopy,
  newSourceProposalRequest,
  sourceDescriptions,
  sourceOrigins,
  usableProposal,
} from "@/lib/journal-source";

// TES-1244 (TES-1240 S4) — new material for one journal heading, reviewed
// instead of inserted. Replaces "Nytt underlag finns" / "Infoga": the doctor
// sees what came in, its source and date, the heading it concerns, the
// current journal text and a proposed updated text, and either approves the
// proposal into the box or keeps his text. Either is a save, which settles
// the flag (journal-revision.service.ts `annotate`).
//
// "Råa frågor och svar får aldrig klistras in i journalen." The proposal is
// only ever the model's text from the per-heading AI (#1383's path); when it
// cannot be had, the proposal box starts empty for him to write, and her
// answers stay in "Vad som har kommit in" above it.

const TEXTAREA =
  "w-full rounded-md border border-surfaceMuted bg-surface p-sm text-sm leading-relaxed text-textPrimary placeholder:text-textDisabled focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20";

type Proposal =
  | { state: "idle" }
  | { state: "loading" }
  | { state: "ready"; removed?: number }
  | { state: "unavailable" };

export function JournalNewSourceReview({
  idPrefix,
  sectionKey,
  heading,
  newSource,
  current,
  edited,
  sectionAi,
  onApprove,
  onKeep,
  onUnauthorized,
  disabled,
}: {
  idPrefix: string;
  sectionKey: string;
  heading: string;
  newSource: JournalNewSource;
  /** The box's text as it stands on screen. */
  current: string;
  /** Whether a clinician has saved this box. */
  edited: boolean;
  sectionAi?: (
    key: string,
    mode: JournalAiMode,
    text: string,
  ) => Promise<JournalSectionAiResult>;
  onApprove: (text: string) => void;
  onKeep: () => void;
  onUnauthorized: () => void;
  disabled: boolean;
}) {
  const copy = newSourceCopy(newSource.requested);
  const [open, setOpen] = useState(false);
  const [proposal, setProposal] = useState<Proposal>({ state: "idle" });
  const [text, setText] = useState("");
  // The box text the proposal was made from: if he types in the box while
  // the review is open, the proposal no longer covers what is there.
  const [basis, setBasis] = useState<string | null>(null);
  const requestRef = useRef(0);

  const propose = useCallback(async () => {
    const request = ++requestRef.current;
    setBasis(current);
    setText("");
    if (!sectionAi) {
      setProposal({ state: "unavailable" });
      return;
    }
    const ask = newSourceProposalRequest({
      current,
      edited,
      incoming: newSource.content,
    });
    if (ask.mode === "compose" && ask.text.length > JOURNAL_SECTION_MAX) {
      setProposal({ state: "unavailable" });
      return;
    }
    setProposal({ state: "loading" });
    try {
      const result = await sectionAi(
        sectionKey,
        ask.mode,
        ask.mode === "compose" ? ask.text : current,
      );
      if (request !== requestRef.current) return;
      const usable = usableProposal(result);
      if (usable === null) {
        setProposal({ state: "unavailable" });
        return;
      }
      setText(usable);
      setProposal({ state: "ready", removed: result.removedSentences });
    } catch (e) {
      if (request !== requestRef.current) return;
      if (e instanceof UnauthorizedError) onUnauthorized();
      setProposal({ state: "unavailable" });
    }
  }, [
    current,
    edited,
    newSource.content,
    sectionAi,
    sectionKey,
    onUnauthorized,
  ]);

  // A newer prefill arriving while the review is open starts it over.
  const revision = newSource.revisionId;
  useEffect(() => {
    requestRef.current++;
    setOpen(false);
    setProposal({ state: "idle" });
    setBasis(null);
  }, [revision]);

  const start = () => {
    setOpen(true);
    void propose();
  };

  const stale = basis !== null && basis !== current;
  const lines = newSource.lines.flatMap(sourceDescriptions);
  const origins = sourceOrigins(newSource.lines);
  const removedNote =
    proposal.state === "ready" ? removedSentencesNote(proposal.removed) : null;
  const proposalId = `journal-${idPrefix}-${sectionKey}-proposal`;

  return (
    <div className="mt-xs rounded-md border border-warning/40 bg-warning/10 p-sm text-xs">
      <div className="flex flex-wrap items-center gap-sm">
        <span className="font-semibold text-textPrimary">{copy.title}</span>
        {open ? null : (
          <button
            type="button"
            onClick={start}
            disabled={disabled}
            className="rounded-md border border-brand px-sm py-[2px] font-semibold text-brand disabled:opacity-50"
          >
            {copy.action}
          </button>
        )}
      </div>
      {open ? (
        <div className="mt-sm flex flex-col gap-sm">
          <dl className="grid grid-cols-[max-content_1fr] gap-x-sm gap-y-[2px] text-textSecondary">
            <dt>Journalrubrik</dt>
            <dd className="font-semibold text-textPrimary">{heading}</dd>
            {origins.length > 0 ? (
              <>
                <dt>Källa och datum</dt>
                <dd className="text-textPrimary">{origins.join("; ")}</dd>
              </>
            ) : null}
          </dl>
          {lines.length > 0 ? (
            <div>
              <p className="font-semibold text-textPrimary">
                Vad som har kommit in
              </p>
              <ul className="mt-[2px] flex flex-col gap-[2px] text-textSecondary">
                {lines.map((l, i) => (
                  <li key={i}>{l}</li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="grid gap-sm md:grid-cols-2">
            <div>
              <p className="mb-[2px] font-semibold text-textPrimary">
                Nuvarande journaltext
              </p>
              <p className="min-h-[3rem] whitespace-pre-wrap rounded-md border border-surfaceMuted bg-surface p-sm text-sm leading-relaxed text-textPrimary">
                {current.trim() ? current : "Ingen text ännu."}
              </p>
            </div>
            <div>
              <label
                htmlFor={proposalId}
                className="mb-[2px] block font-semibold text-textPrimary"
              >
                Föreslagen uppdaterad journaltext
              </label>
              <textarea
                id={proposalId}
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={Math.min(14, Math.max(3, text.split("\n").length + 1))}
                maxLength={JOURNAL_SECTION_MAX}
                disabled={proposal.state === "loading"}
                placeholder={
                  proposal.state === "loading" ? "Tar fram förslag…" : undefined
                }
                className={TEXTAREA}
              />
              {proposal.state === "unavailable" ? (
                <p className="mt-[2px] text-textSecondary">
                  Inget förslag kunde tas fram. Skriv den uppdaterade texten
                  själv.
                </p>
              ) : null}
              {removedNote ? (
                <p className="mt-[2px] text-textSecondary">{removedNote}</p>
              ) : null}
            </div>
          </div>
          {stale ? (
            <p className="text-warning">
              Journaltexten har ändrats sedan förslaget togs fram.{" "}
              <button
                type="button"
                onClick={() => void propose()}
                className="underline"
              >
                Ta fram nytt förslag
              </button>
            </p>
          ) : null}
          <div className="flex flex-wrap gap-xs">
            <button
              type="button"
              onClick={() => onApprove(text)}
              disabled={
                disabled ||
                stale ||
                proposal.state === "loading" ||
                text.trim() === ""
              }
              className="rounded-md border border-brand bg-brand px-sm py-[3px] font-semibold text-white disabled:opacity-50"
            >
              {approveNewSourceLabel(heading)}
            </button>
            <button
              type="button"
              onClick={onKeep}
              disabled={disabled}
              className="rounded-md border border-surfaceMuted px-sm py-[3px] text-textSecondary hover:text-textPrimary disabled:opacity-50"
            >
              {KEEP_JOURNAL_TEXT}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
